import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Breadcrumbs,
  Link,
  Divider,
  Stack,
  TextField,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "../../context/SnackbarContext";
import { stockService } from "../../services/stock/stockService";
import { userService } from "../../services/user/userService";
import { Product, Society, StockStatusResponse } from "../../types/api.types";
import DataTable from "../../components/DataTable";
import { productService } from "../../services/product/productService";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/es";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

interface StockFormData {
  estimatedArribalDate: string;
  arribalDate: string;
  observation: string;
  companyId: string;
  code: string;
  percentageSafety: string;
}

interface ArticleFormData {
  nombre: string;
  productId: string;
  code: string;
  cantidad: string;
}

interface StockDetail {
  stockHeaderId: string;
  code: string;
  observation: string;
  estimatedArribalDate: string;
  arribalDate?: string;
  percentageSafety: string;
  company: string;
  status: string;
  createDate: string;
  companyId: string;
}

/** Resto estimado: cantidad menos el fallo aplicado como porcentaje sobre la cantidad. */
function computeArticleRest(quantity: number, failurePercent: number): number {
  if (!Number.isFinite(quantity) || quantity < 0) return 0;
  const pct = Number.isFinite(failurePercent) ? failurePercent : 0;
  return Math.round(quantity - (quantity * pct) / 100);
}

const StockArribalPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [stockData, setStockData] = useState<StockDetail | null>(null);
  const [societies, setSocieties] = useState<Society[]>([]);
  const [statuses, setStatuses] = useState<StockStatusResponse[]>([]);

  const { showSnackbar } = useSnackbar();
  const [productSearch, setProductSearch] = useState<string>("");
  const [articlePage, setArticlePage] = useState<number>(0);
  const [articleRowsPerPage] = useState<number>(10);
  const [articles, setArticles] = useState<any[]>([]);
  const [articlesTotal, setArticlesTotal] = useState<number>(0);
  const [loadingArticles, setLoadingArticles] = useState<boolean>(false);

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [editingArticle, setEditingArticle] = useState<any | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [productInputValue, setProductInputValue] = useState<string>("");
  const [loadingProducts, setLoadingProducts] = useState<boolean>(false);
  const [productOpen, setProductOpen] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<StockFormData>({
    defaultValues: {
      estimatedArribalDate: "",
      arribalDate: "",
      observation: "",
      companyId: "",
      code: "",
      percentageSafety: "",
    },
  });

  const {
    control: articleControl,
    handleSubmit: handleArticleSubmit,
    formState: { errors: articleErrors },
    reset: resetArticleForm,
    setValue: setArticleValue,
    watch: watchArticle,
  } = useForm<ArticleFormData>({
    defaultValues: {
      nombre: "",
      code: "",
      productId: "",
      cantidad: "",
    },
  });

  const cantidadWatched = watchArticle("cantidad");
  const failurePct = stockData?.percentageSafety ?? "0";

  // Búsqueda asíncrona de productos
  useEffect(() => {
    let active = true;

    if (!productOpen) {
      return undefined;
    }

    const fetchProducts = async () => {
      setLoadingProducts(true);
      try {
        const productsResponse = await productService.getProducts(
          0,
          30,
          productInputValue,
        );

        if (active) {
          setProducts(productsResponse?.products || []);
        }
      } catch (error: any) {
        if (active) {
          showSnackbar(
            error.response?.data?.message || "Error al buscar productos",
            "error",
            4000,
          );
          setProducts([]);
        }
      } finally {
        if (active) {
          setLoadingProducts(false);
        }
      }
    };

    const delayDebounceFn = setTimeout(() => {
      fetchProducts();
    }, 300);

    return () => {
      active = false;
      clearTimeout(delayDebounceFn);
    };
  }, [productInputValue, productOpen, showSnackbar]);

  const articleColumns = [
    {
      header: "Código Producto",
      key: "code",
    },
    {
      header: "Nombre",
      key: "description",
    },
    {
      header: "Cantidad",
      key: "quantity",
    },
    {
      header: "Resto",
      key: "rest",
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const societiesResponse = await userService.getCompanies();
        setSocieties(societiesResponse as any[]);

        const statusesResponse = await stockService.getStatuses();
        setStatuses(statusesResponse);
      } catch (error: any) {
        showSnackbar(
          error.response?.data?.message || "Error al cargar datos",
          "error",
          4000,
        );
      }
    };
    fetchData();
  }, []);

  // Cargar items del stock con paginación y búsqueda
  useEffect(() => {
    const fetchItems = async () => {
      if (!id) return;

      try {
        setLoadingArticles(true);
        const response = await stockService.getItems(
          id,
          articlePage,
          articleRowsPerPage,
          productSearch,
        );
        setArticles(response?.stockItemResponses || response?.items || []);
        setArticlesTotal(response?.total || 0);
      } catch (error: any) {
        showSnackbar(
          error.response?.data?.message || "Error al cargar los items",
          "error",
          4000,
        );
        setArticles([]);
        setArticlesTotal(0);
      } finally {
        setLoadingArticles(false);
      }
    };

    // Debounce para la búsqueda
    const delayDebounceFn = setTimeout(() => {
      fetchItems();
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [id, articlePage, articleRowsPerPage, productSearch, showSnackbar]);

  const onGetStockById = async () => {
    const response = await stockService.getStockById(id as string);
    setStockData(response as any);
  };

  useEffect(() => {
    if (id) {
      onGetStockById();
    }
  }, [id]);

  useEffect(() => {
    if (stockData) {
      reset({
        code: stockData.code || "",
        companyId: stockData.companyId || "",
        percentageSafety: stockData.percentageSafety || "",
        estimatedArribalDate: stockData.estimatedArribalDate
          ? new Date(stockData.estimatedArribalDate).toISOString().split("T")[0]
          : "",
        arribalDate: stockData.arribalDate
          ? new Date(stockData.arribalDate).toISOString().split("T")[0]
          : "",
        observation: stockData.observation || "",
      });
    }
  }, [stockData, reset]);

  const handleCancel = () => {
    navigate("/stock");
  };

  const handleClearSearch = () => {
    setProductSearch("");
    setArticlePage(0);
  };

  const handleArticlePageChange = (page: number) => {
    const newPage = page - 1; // DataTable usa páginas basadas en 1, API usa basadas en 0
    setArticlePage(newPage);
  };

  const onSubmit = async (data: StockFormData) => {
    if (!id) return;

    try {
      setIsSaving(true);

      // Actualizar el encabezado del stock
      let response: any = await stockService.updateStock(id, {
        observation: data.observation,
        arribalDate: new Date(data.arribalDate).toISOString(),
      });

      // Filtrar los artículos que tienen type: "EDIT" o "ADD"
      const itemsToUpdate = articles.filter(
        (article) => article.type === "EDIT" || article.type === "ADD",
      );

      // Si hay items para actualizar, llamar al endpoint
      if (itemsToUpdate.length > 0) {
        const itemsPayload = itemsToUpdate.map((article) => ({
          stockHeaderId: id,
          stockItemId: article.stockItemId,
          productId: article.productId,
          quantity: article.quantity,
        }));

        await stockService.updateStockItems(itemsPayload);
      }

      showSnackbar("Stock actualizado correctamente", "success", 3000);
      navigate("/stock");
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message || "Error al actualizar el stock",
        "error",
        4000,
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!stockData) {
    return null;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Form Card */}
      <Card elevation={2}>
        <CardContent sx={{ p: 4 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs sx={{ mb: 3 }}>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={() => navigate("/home")}
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              Inicio
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={() => navigate("/stock")}
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              Stocks
            </Link>
            <Typography
              color="text.primary"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              Arribar Stock
            </Typography>
          </Breadcrumbs>

          {/* Page Header */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Arribar Stock
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Complete la fecha de arribo del stock
            </Typography>
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            sx={{
              display: "flex",
              gap: 4,
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            {/* Form Fields */}
            <Box sx={{ flex: 1 }}>
              <Stack spacing={3}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      label="Código (no editable)"
                      value={stockData.code || ""}
                      fullWidth
                      InputProps={{
                        readOnly: true,
                      }}
                      disabled
                    />
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <TextField
                      label="Estado (no editable)"
                      value={stockData.status || ""}
                      fullWidth
                      InputProps={{
                        readOnly: true,
                      }}
                      disabled
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Controller
                      name="estimatedArribalDate"
                      control={control}
                      rules={{
                        required: "La fecha estimada de llegada es requerida",
                      }}
                      render={({ field, fieldState }) => (
                        <LocalizationProvider
                          dateAdapter={AdapterDayjs}
                          adapterLocale="es"
                        >
                          <DatePicker
                            label="Fecha Estimada de Llegada"
                            format="DD/MM/YYYY"
                            value={field.value ? dayjs(field.value) : null}
                            onChange={(newValue: Dayjs | null) => {
                              field.onChange(
                                newValue ? newValue.format("YYYY-MM-DD") : "",
                              );
                            }}
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                                error: !!fieldState.error,
                                helperText: fieldState.error?.message,
                              },
                            }}
                          />
                        </LocalizationProvider>
                      )}
                    />
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Controller
                      name="arribalDate"
                      control={control}
                      rules={{
                        required: "La fecha de llegada es requerida",
                      }}
                      render={({ field, fieldState }) => (
                        <LocalizationProvider
                          dateAdapter={AdapterDayjs}
                          adapterLocale="es"
                        >
                          <DatePicker
                            label="Fecha de Llegada"
                            format="DD/MM/YYYY"
                            value={field.value ? dayjs(field.value) : null}
                            onChange={(newValue: Dayjs | null) => {
                              field.onChange(
                                newValue ? newValue.format("YYYY-MM-DD") : "",
                              );
                            }}
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                                error: !!fieldState.error,
                                helperText: fieldState.error?.message,
                              },
                            }}
                          />
                        </LocalizationProvider>
                      )}
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      label="Sociedad (no editable)"
                      value={stockData.company || ""}
                      fullWidth
                      InputProps={{
                        readOnly: true,
                      }}
                      disabled
                    />
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <TextField
                      label="Porcentaje de Fallo (no editable)"
                      value={stockData.percentageSafety || 0}
                      fullWidth
                      InputProps={{
                        readOnly: true,
                      }}
                      disabled
                    />
                  </Box>
                </Box>

                <Box>
                  <Controller
                    name="observation"
                    control={control}
                    rules={{
                      required: "La observación es requerida",
                      minLength: {
                        value: 10,
                        message:
                          "La observación debe tener al menos 10 caracteres",
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Observación"
                        fullWidth
                        multiline
                        rows={4}
                        error={!!errors.observation}
                        helperText={errors.observation?.message}
                        placeholder="Ingrese observaciones sobre el stock..."
                      />
                    )}
                  />
                </Box>
              </Stack>
            </Box>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Articles Section */}
          <Box sx={{ mt: 4 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography variant="h6" component="h2">
                Listado de Productos
              </Typography>
            </Box>

            {/* Filtro de búsqueda */}
            <Box sx={{ mb: 3 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ alignItems: "center" }}
              >
                <TextField
                  label="Buscar producto"
                  value={productSearch}
                  onChange={(e) => {
                    setProductSearch(e.target.value);
                    setArticlePage(0);
                  }}
                  placeholder="Buscar por código o nombre..."
                  fullWidth
                  size="small"
                />
                <Button
                  variant="outlined"
                  onClick={handleClearSearch}
                  sx={{ minWidth: 120, height: 40 }}
                >
                  Limpiar
                </Button>
              </Stack>
            </Box>

            <DataTable
              columns={articleColumns}
              data={articles}
              page={articlePage + 1}
              isLoading={loadingArticles}
              onChange={handleArticlePageChange}
              total={articlesTotal}
            />
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              mt: 3,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{ minWidth: 120 }}
              disabled={isLoading || isSaving}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit(onSubmit)}
              sx={{ minWidth: 120 }}
              disabled={isLoading || isSaving}
            >
              {isSaving ? "Guardando..." : "Guardar Cambios"}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default StockArribalPage;
